import Vue from "vue";
import i18n from "../i18n";
import { runOptionalAnalytics as safely } from "../utils/optionalAnalytics";
import { createAnalyticsTracking, trackingConfig } from "../services/analyticsTracking";
import {
  clearAnalyticsIdentity,
  readAnalyticsConsent,
  writeAnalyticsConsent,
} from "../utils/analyticsConsent";

const PROJECT_TOKEN =
  process.env.VUE_APP_POSTHOG_PROJECT_TOKEN || "phc_vaGu2QxeRK9HCMxqGYPEuyFJxGncdUywauDd6XDum7NU";
const API_HOST = process.env.VUE_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
const APP_ENVIRONMENT = process.env.NODE_ENV || "development";
export const analyticsConsent = Vue.observable({ status: readAnalyticsConsent() });
// Evaluate the SDK inside the protected installation path, not at module import.
const loadPostHogClient = () => require("posthog-js").default;
let posthogClient;
let tracking;
let clientReady = false;
let consentChannel;

function noOp() {}

function disablePostHog() {
  clientReady = false;
}

function addEnvironment(event) {
  // Drop events if initialization or a consent transition failed. Never send
  // with an identity whose persistence no longer matches the visitor's choice.
  // The consent check fails closed: an unreadable choice must not be sent under.
  const consentMatches = safely(
    () => clientReady && !tracking.changing && readAnalyticsConsent() === tracking.status,
    false
  );
  if (!consentMatches) return null;
  // A malformed event has nothing to enrich, so it is dropped. Enrichment
  // failing for any other reason must not discard a legitimate event: fall
  // back to sending it without the extra properties.
  if (!event || !event.properties) return null;
  return safely(() => {
    event.properties.analytics_mode = tracking.mode;
    event.properties.analytics_consent = tracking.status;
    event.properties.app_environment = APP_ENVIRONMENT;
    event.properties.app_language = i18n.locale;
    if (typeof window !== "undefined") {
      // Viewport category, independent of the page's responsive layout.
      event.properties.layout_breakpoint = window.matchMedia("(max-width: 768px)").matches
        ? "mobile"
        : window.matchMedia("(max-width: 992px)").matches
        ? "tablet"
        : "desktop";
    }
    return event;
  }, event);
}

function synchronizeConsent() {
  const status = readAnalyticsConsent();
  analyticsConsent.status = status;
  if (clientReady && status !== tracking.status) {
    // Expired/removed consent also clears a previously persistent identity.
    safely(() => tracking.setConsent(status), undefined, disablePostHog);
    if (!clientReady && status !== "granted") safely(() => clearAnalyticsIdentity(PROJECT_TOKEN));
  }
}

function updateAnalyticsConsent(status) {
  const previousStatus = readAnalyticsConsent();
  // Saving the UI choice is independent of the SDK, broadcasting, and Google.
  writeAnalyticsConsent(status);
  analyticsConsent.status = status;
  synchronizeConsent();
  if (!clientReady && status !== "granted") safely(() => clearAnalyticsIdentity(PROJECT_TOKEN));
  if (status !== previousStatus) {
    // Send after saving the choice so before_send sees the new state. Earlier
    // events retain their original consent label and are linked by distinct_id.
    posthog.capture("analytics_consent_changed", {
      consent_action:
        status === "granted" ? "accept" : status === "denied" ? "decline" : "withdraw",
      previous_analytics_consent: previousStatus,
    });
  }
  safely(() => {
    if (consentChannel) consentChannel.postMessage("changed");
  });
  safely(() => {
    if (status === "granted" && typeof window.initializeGTM === "function")
      return window.initializeGTM();
  });
}

export function setAnalyticsConsent(granted) {
  return safely(
    () => updateAnalyticsConsent(granted ? "granted" : "denied"),
    undefined,
    disablePostHog
  );
}

export function revokeAnalyticsConsent() {
  // The existing withdrawal control clears the choice so the banner can reappear.
  return safely(() => updateAnalyticsConsent("pending"), undefined, disablePostHog);
}

// Safe, small API for feature flags and experiment goal events.
const posthog = {
  capture(eventName, properties, options) {
    return safely(() => {
      synchronizeConsent();
      if (!clientReady) return;
      return posthogClient.capture(eventName, properties, options);
    }, undefined);
  },

  getFeatureFlag(key, fallback = "control") {
    const value = safely(() => {
      synchronizeConsent();
      if (!clientReady) return;
      return posthogClient.getFeatureFlag(key);
    }, undefined);
    return typeof value === "string" || typeof value === "boolean" ? value : fallback;
  },

  isFeatureEnabled(key) {
    const value = safely(() => {
      synchronizeConsent();
      if (!clientReady) return false;
      return posthogClient.isFeatureEnabled(key);
    }, false);
    return value === true;
  },

  onFeatureFlags(callback) {
    const unsubscribe = safely(() => {
      if (!clientReady) return noOp;
      return posthogClient.onFeatureFlags((flags, variants, context) => {
        safely(() => {
          synchronizeConsent();
          if (clientReady) return callback(flags, variants, context);
        });
      });
    }, noOp);
    return () =>
      safely(() => {
        if (typeof unsubscribe === "function") return unsubscribe();
      });
  },
};

export { posthog };

export default {
  install(Vue) {
    // Install the safe facade even when loading or initializing the SDK fails.
    Vue.prototype.$posthog = posthog;
    safely(() => {
      if (analyticsConsent.status !== "granted") clearAnalyticsIdentity(PROJECT_TOKEN);
    });
    safely(
      () => {
        posthogClient = loadPostHogClient();
        tracking = createAnalyticsTracking(posthogClient, analyticsConsent.status);
        clientReady = true;
        return posthogClient.init(PROJECT_TOKEN, {
          api_host: API_HOST,
          defaults: "2026-05-30",
          ...trackingConfig(analyticsConsent.status),
          // App consent governs optional identity persistence, not the SDK's
          // full analytics opt-out. Keep those independent so rejection can retain
          // the same in-memory funnel without granting cookie consent to PostHog.
          person_profiles: "never",
          before_send: addEnvironment,
          // Only pageviews, flag exposures, and explicit capture() calls are sent.
          autocapture: false,
          // Router navigation captures pageviews once. Consent transitions must not
          // introduce a second pageview or reset the pending visit's funnel.
          capture_pageview: false,
          capture_pageleave: false,
          capture_dead_clicks: false,
          capture_exceptions: false,
          capture_heatmaps: false,
          capture_performance: false,
          disable_session_recording: true,
          disable_surveys: true,
        });
      },
      undefined,
      disablePostHog
    );

    if (typeof BroadcastChannel !== "undefined") {
      safely(() => {
        consentChannel = new BroadcastChannel("list2go-analytics-consent");
        consentChannel.onmessage = () => safely(synchronizeConsent);
      });
    }
    safely(() =>
      window.addEventListener("focus", () => safely(synchronizeConsent, undefined, disablePostHog))
    );
    safely(() =>
      document.addEventListener("visibilitychange", () => {
        safely(
          () => {
            if (!document.hidden) synchronizeConsent();
          },
          undefined,
          disablePostHog
        );
      })
    );
  },
};
